import { type FormEvent, useMemo, useState } from "react";
import { Link, NavLink, Route, Routes } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { format } from "date-fns";
import {
  getAdminProducts,
  getBuilderSubmissions,
  getEnquiries,
  getNewsletterSignups,
  getOrders,
  markOrderFulfilled,
  setEnquiryClosed,
  updateProductOverride,
  type DemoEnquiry,
  type DemoOrder
} from "./data";

const sessionKey = "trio-admin-session";
const adminNavItems = [
  ["Overview", "/admin"],
  ["Orders", "/admin/orders"],
  ["Products", "/admin/products"],
  ["Enquiries", "/admin/enquiries"],
  ["Audience", "/admin/audience"]
] as const;

function formatDate(value: string) {
  return format(new Date(value), "dd MMM yyyy, HH:mm");
}

function EmptyState({ children }: { children: string }) {
  return <div className="admin-empty"><span aria-hidden="true">· · ·</span><p>{children}</p></div>;
}

function AdminLogin({ onLogin }: { onLogin: () => void }) {
  const [pin, setPin] = useState("");
  const [error, setError] = useState("");

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (pin !== "2026") {
      setError("That demo PIN is not quite right.");
      return;
    }
    localStorage.setItem(sessionKey, "demo");
    onLogin();
  };

  return (
    <main className="admin-login">
      <div>
        <Link className="admin-brand" to="/">
          <img src="/logo-mark.png" srcSet="/apple-touch-icon.png 180w, /logo-mark.png 1254w" sizes="52px" alt="" />
          <span>TRIO ATELIER <small>STUDIO DESK</small></span>
        </Link>
        <p className="eyebrow">Demo dashboard</p>
        <h1>A quiet place to keep track.</h1>
        <p>This local preview is for testing the founder workflow. It is not production authentication.</p>
        <form onSubmit={submit}>
          <label htmlFor="admin-pin">Demo PIN</label>
          <input id="admin-pin" value={pin} onChange={(event) => setPin(event.target.value)} inputMode="numeric" autoComplete="off" />
          <small>Use <b>2026</b> for demo access.</small>
          <AnimatePresence>{error && <motion.span className="field-error" initial={{ opacity: 0, y: -4 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}>{error}</motion.span>}</AnimatePresence>
          <button className="button" type="submit">Enter the studio desk ↗</button>
        </form>
      </div>
    </main>
  );
}

function Metric({ label, value, note }: { label: string; value: number; note: string }) {
  return <article className="admin-metric"><span>{label}</span><b>{value}</b><small>{note}</small></article>;
}

function DashboardHome() {
  const orders = getOrders();
  const enquiries = getEnquiries();
  const subscribers = getNewsletterSignups();
  const today = new Date().toDateString();
  return (
    <>
      <header className="admin-page-heading"><p className="eyebrow">Overview</p><h1>Good morning, creators.</h1><p>Everything here is stored only in this browser for the demo.</p></header>
      <section className="admin-metrics">
        <Metric label="Orders today" value={orders.filter((order) => new Date(order.createdAt).toDateString() === today).length} note="Demo checkout records" />
        <Metric label="Open enquiries" value={enquiries.filter((item) => item.status === "open").length} note="Contact and corporate" />
        <Metric label="On the list" value={subscribers.length} note="Newsletter signups" />
        <Metric label="To fulfil" value={orders.filter((order) => order.status === "new").length} note="Needs attention" />
      </section>
      <section className="admin-panel"><div className="admin-panel-heading"><div><p className="eyebrow">Recent orders</p><h2>What needs preparing</h2></div><Link to="/admin/orders">View all ↗</Link></div><OrderRows orders={orders.slice(0, 4)} onChange={() => undefined} compact /></section>
    </>
  );
}

function OrderRows({ orders, onChange, compact = false }: { orders: DemoOrder[]; onChange: (orders: DemoOrder[]) => void; compact?: boolean }) {
  if (!orders.length) return <EmptyState>Orders placed through the demo checkout will appear here.</EmptyState>;
  return <div className="admin-table-wrap"><table><thead><tr><th>Order</th><th>Customer</th><th>Placed</th><th>Total</th><th>Status</th>{!compact && <th>Action</th>}</tr></thead><tbody>{orders.map((order) => <tr key={order.id}><td><b>{order.id}</b><small>{order.items.map((item) => `${item.quantity}× ${item.name}`).join(", ")}</small></td><td>{order.customer.name}<small>{order.customer.email}</small></td><td>{formatDate(order.createdAt)}</td><td>£{order.total.toFixed(2)}</td><td><span className={`admin-status admin-status--${order.status}`}>{order.status}</span></td>{!compact && <td><button type="button" onClick={() => onChange(markOrderFulfilled(order.id, order.status !== "fulfilled"))}>{order.status === "fulfilled" ? "Reopen" : "Mark fulfilled"}</button></td>}</tr>)}</tbody></table></div>;
}

function OrdersPage() {
  const [orders, setOrders] = useState(getOrders);
  const [filter, setFilter] = useState<"all" | "new" | "fulfilled">("all");
  const [oldestFirst, setOldestFirst] = useState(false);
  const visible = useMemo(() => orders.filter((order) => filter === "all" || order.status === filter).sort((a, b) => (oldestFirst ? 1 : -1) * a.createdAt.localeCompare(b.createdAt)), [orders, filter, oldestFirst]);
  return <><header className="admin-page-heading"><p className="eyebrow">Orders</p><h1>From checkout to doorstep.</h1></header><div className="admin-toolbar"><label>Status<select value={filter} onChange={(event) => setFilter(event.target.value as typeof filter)}><option value="all">All orders</option><option value="new">New</option><option value="fulfilled">Fulfilled</option></select></label><button type="button" onClick={() => setOldestFirst((value) => !value)}>{oldestFirst ? "Newest first" : "Oldest first"}</button></div><section className="admin-panel"><OrderRows orders={visible} onChange={setOrders} /></section></>;
}

function ProductsPage() {
  const [items, setItems] = useState(getAdminProducts);
  const save = (slug: string, description: string, featured: boolean) => {
    updateProductOverride(slug, { description, featured });
    setItems(getAdminProducts());
  };
  return <><header className="admin-page-heading"><p className="eyebrow">Products</p><h1>The collection, at a glance.</h1><p>Edits are saved as local overrides; the static catalogue remains untouched.</p></header><section className="admin-product-list">{items.map((product) => <article key={product.slug}><img src={product.image} alt="" /><div><span className="eyebrow">{product.mood} · £{product.price}</span><h2>{product.name}</h2><label>Description<textarea value={product.description} onChange={(event) => setItems((current) => current.map((item) => item.slug === product.slug ? { ...item, description: event.target.value } : item))} /></label><label className="admin-check"><input type="checkbox" checked={product.featured} onChange={(event) => setItems((current) => current.map((item) => item.slug === product.slug ? { ...item, featured: event.target.checked } : item))} /> Feature on storefront</label><button type="button" onClick={() => save(product.slug, product.description, product.featured)}>Save local override</button></div></article>)}</section></>;
}

function EnquiryRows({ initial }: { initial: DemoEnquiry[] }) {
  const [items, setItems] = useState(initial);
  if (!items.length) return <EmptyState>Contact and corporate enquiries will collect here.</EmptyState>;
  return <div className="admin-table-wrap"><table><thead><tr><th>From</th><th>Type</th><th>Message</th><th>Received</th><th>Action</th></tr></thead><tbody>{items.map((item) => <tr key={item.id}><td><b>{item.name}</b><small>{item.email}</small></td><td>{item.kind}</td><td>{item.message}</td><td>{formatDate(item.createdAt)}</td><td><button type="button" onClick={() => setItems(setEnquiryClosed(item.id, item.status !== "closed"))}>{item.status === "closed" ? "Reopen" : "Close"}</button></td></tr>)}</tbody></table></div>;
}

function EnquiriesPage() {
  return <><header className="admin-page-heading"><p className="eyebrow">Enquiries</p><h1>Conversations waiting.</h1></header><section className="admin-panel"><EnquiryRows initial={getEnquiries()} /></section></>;
}

function AudiencePage() {
  const signups = getNewsletterSignups();
  const builders = getBuilderSubmissions();
  return <><header className="admin-page-heading"><p className="eyebrow">Audience</p><h1>Interest, thoughtfully gathered.</h1></header><div className="admin-two-up"><section className="admin-panel"><div className="admin-panel-heading"><div><p className="eyebrow">Newsletter</p><h2>{signups.length} signups</h2></div></div>{signups.length ? <ul className="admin-record-list">{signups.map((item) => <li key={item.id}><span>{item.email}</span><small>{formatDate(item.createdAt)}</small></li>)}</ul> : <EmptyState>Newsletter signups will appear here.</EmptyState>}</section><section className="admin-panel"><div className="admin-panel-heading"><div><p className="eyebrow">Custom builds</p><h2>{builders.length} compositions</h2></div></div>{builders.length ? <ul className="admin-record-list">{builders.map((item) => <li key={item.id}><span>{item.items.join(", ")} · {item.colour}</span><b>£{item.total}</b><small>{formatDate(item.createdAt)}</small></li>)}</ul> : <EmptyState>Submitted hamper compositions will appear here.</EmptyState>}</section></div></>;
}

export default function AdminApp() {
  const [authenticated, setAuthenticated] = useState(() => localStorage.getItem(sessionKey) === "demo");
  if (!authenticated) return <AdminLogin onLogin={() => setAuthenticated(true)} />;
  const signOut = () => { localStorage.removeItem(sessionKey); setAuthenticated(false); };
  return <div className="admin-shell"><aside className="admin-sidebar"><Link className="admin-brand" to="/"><img src="/logo-mark.png" srcSet="/apple-touch-icon.png 180w, /logo-mark.png 1254w" sizes="52px" alt="" /><span>TRIO ATELIER <small>STUDIO DESK</small></span></Link><nav>{adminNavItems.map(([label, path]) => <NavLink key={path} to={path} end={path === "/admin"}>{label}</NavLink>)}</nav><div><p>Demo access<br /><small>Local browser data only</small></p><button type="button" onClick={signOut}>Sign out</button></div></aside><main className="admin-main"><Routes><Route index element={<DashboardHome />} /><Route path="orders" element={<OrdersPage />} /><Route path="products" element={<ProductsPage />} /><Route path="enquiries" element={<EnquiriesPage />} /><Route path="audience" element={<AudiencePage />} /><Route path="*" element={<DashboardHome />} /></Routes></main></div>;
}
